import { FC, useMemo } from 'react'
import shp from 'shpjs'
import { GeoJSON2SVG } from 'geojson2svg'
import { FileUpload } from 'src/components/file-upload'
import { bbox } from '@turf/turf'
import styled from '@emotion/styled'
import { Colors, Shapes } from 'src/components/theme'
import { Close } from '@emotion-icons/material/Close'
import { Typography } from '@mui/material'
import { growl } from 'src/components/growl'
import { browserLogger } from 'src/utils/browser-logger'

const PreviewWrapper = styled.div`
  border: 1px dashed ${Colors.blueDark};
  border-radius: ${Shapes.borderRadius}px;
  overflow: hidden;
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  position: relative;
`

const CloseButton = styled(Close)`
  position: absolute;
  right: 16px;
  top: 16px;
  z-index: 2;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const Preview = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  z-index: 1;

  svg {
    height: 80%;
    overflow: hidden;

    path {
      stroke-width: 1.5px;
    }
  }
`

type ShapefileUploaderProps = {
  geojson?: shp.FeatureCollectionWithFilename | shp.FeatureCollectionWithFilename[] | null
  onUpload?: (geojson: shp.FeatureCollectionWithFilename | shp.FeatureCollectionWithFilename[] | null) => void
}

export const ShapefileUploader: FC<ShapefileUploaderProps> = ({ geojson, onUpload }) => {
  const handleFileUpload = async (file: File | undefined) => {
    if (!file) {
      return
    }

    const arrayBuffer = await file.arrayBuffer()

    try {
      const result = await shp(arrayBuffer)
      onUpload?.(result)
    } catch (error) {
      browserLogger.error('Error converting SHP:', error)
      growl({
        text: 'Error converting SHP',
        severity: 'error',
      })
    }
  }

  const onCancel = () => {
    onUpload?.(null)
  }

  const svg = useMemo(() => {
    if (!geojson) {
      return null
    }

    const geoJsonWithProps: GeoJSON.FeatureCollection<GeoJSON.Polygon> = {
      type: 'FeatureCollection',
      features: (geojson as GeoJSON.FeatureCollection<GeoJSON.Polygon>).features.map((feature) => ({
        ...feature,
        properties: {
          type: 'Polygon',
          fill: Colors.customAreaFill,
          stroke: Colors.customAreaStroke,
          'stroke-width': '2',
        },
      })),
    }

    const VectorSquare = bbox(geoJsonWithProps)

    const converter = new GeoJSON2SVG({
      viewportSize: {
        width: 100,
        height: 100,
      },
      mapExtent: {
        left: VectorSquare[0],
        right: VectorSquare[2],
        top: VectorSquare[3],
        bottom: VectorSquare[1],
      },
    })

    const svg = converter.convert(geoJsonWithProps as GeoJSON.FeatureCollection, {
      // @ts-ignore
      attributes: ['properties.fill', 'properties.stroke', 'properties.stroke-width'],
    })

    const svgStr = `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">${svg}</svg>`
    return svgStr
  }, [geojson])

  const fileName = useMemo(() => {
    if (!geojson) {
      return null
    }

    if (Array.isArray(geojson)) {
      return geojson[0].fileName
    }

    return geojson.fileName
  }, [geojson])

  return (
    <div>
      {svg ? (
        <PreviewWrapper>
          <CloseButton size={24} color={Colors.neutral500} onClick={onCancel} />
          <Preview dangerouslySetInnerHTML={{ __html: svg }} />
          <Typography variant="textmdm" color={Colors.neutral500}>
            {fileName}
          </Typography>
        </PreviewWrapper>
      ) : (
        <FileUpload accept=".zip" onChange={handleFileUpload} uploadText="Upload a zipped shapefile" />
      )}
    </div>
  )
}

export default ShapefileUploader
